import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { OrderStatus, OrderWithContext } from '@/models';
import { CharitableCampaign, DonationType } from '@/models/donations';

@Component({
  name: 'CmgCharitableDonation',
})
export default class CmgCharitableDonation extends Vue {
  donationGiven = false;
  isProcessingDonation = false;

  @Prop({ required: true, type: Object })
  order!: OrderWithContext;

  get isOrderSubmitted(): boolean {
    if (!this.order) {
      return false;
    }

    return this.order.order.orderStatus == OrderStatus.Submitted;
  }

  get donationAmount(): number {
    if (!this.order) {
      return 0;
    }

    return this.order.order.orderDonationAmount;
  }

  get donationHeader(): string {
    if (!this.charitableCampaign) {
      return '';
    }

    if (this.isOrderSubmitted) {
      return 'Charitable Donation';
    }

    // Going with generic 'Donate' if it is not of type RoundUp
    return this.charitableCampaign.donationType == DonationType.RoundUp ? 'Round Up & Donate' : 'Donate';
  }

  get charityLogo(): string {
    if (!this.charitableCampaign?.images) {
      return '';
    }

    const image = this.charitableCampaign.images.find(img => img.imageCategory.toUpperCase() == 'WEB');
    return image?.imageUrl ?? '';
  }

  get charityLogoAlt(): string {
    return `${this.charitableCampaign?.charityName} logo`;
  }

  get infoIcon(): string {
    return this.$appStore.state.cms.images.icons.info.default;
  }

  get learnMoreUrl(): string {
    if (!this.charitableCampaign) {
      return '';
    }

    return this.charitableCampaign.learnMoreURL;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get charitableCampaign(): CharitableCampaign | null {
    return this.$appStore.state.order.charitableCampaign;
  }

  get donationActionText(): string {
    if (!this.charitableCampaign) {
      return '';
    }

    return `Yes, I want to ${this.charitableCampaign.donationType == DonationType.RoundUp ? 'round up' : 'donate'}`;
  }

  get orderCampaignId(): string {
    if (!this.order.order.donations || !this.order.order.donations.length) {
      return '';
    }

    return this.order.order.donations[0].charitableCampaignId;
  }

  async mounted() {
    // delete existing donation if the chosen campaign is different from the campaign in the Vuex store.
    if (this.charitableCampaign && this.orderCampaignId && this.charitableCampaign.campaignId != this.orderCampaignId) {
      await this.$services.order.deleteDonation(this.order, this.orderCampaignId);
    }

    this.donationGiven = !!this.donationAmount;
  }

  @Watch('order', { immediate: true })
  onOrderUpdated(order: OrderWithContext | null) {
    if (!order) {
      return;
    }

    if (!order.order.orderDonationAmount) {
      this.donationGiven = false;
    }
  }

  formatPrice(price: number | null): string {
    return this.$services.utils.formatPrice(price);
  }

  async onToggleAddDonation() {
    if (!this.charitableCampaign) {
      return;
    }

    this.$services.ui.setAppPepperVisibility(true);

    try {
      if (this.donationGiven) {
        await this.$services.order.createDonation(this.order, { charitableCampaignId: this.charitableCampaign.campaignId, countryCode: this.countryCode });
      } else {
        await this.$services.order.deleteDonation(this.order, this.orderCampaignId);
      }
    } catch (error: any) {
      this.$services.logger.error('Could not process donation.', error);
      // reset the donation check box to the state before the error.
      this.donationGiven = !this.donationGiven;
    }

    this.$services.ui.setAppPepperVisibility(false);
  }

  onClickInfo(event: any): void {
    if (!this.charitableCampaign) {
      return;
    }
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('basicError', true, {
      header: this.charitableCampaign.charityName,
      content: this.charitableCampaign.charityDescription,
      options: [
        {
          text: 'Got It',
          type: ButtonType.Primary,
          onClick: () => {
            this.$services.ui.setModalVisibility('basicError', false);
          },
        },
      ],
    });
    this.$services.ui.firstFocus();
  }
}
